import { useNavigate } from 'react-router-dom';
import { Box, Grid } from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  useGetMerchantConfigsByAMCQuery,
  useCreateMerchantConfigMutation,
  useUpdateMerchantConfigMutation,
  useGetAllAggregatorsQuery,
} from '../services/gatewayConfigApi';
import { useAMC } from '../context/AMCContext';
import { FormikTextField } from '../components/FormikTextField';
import { FormikTagsInput } from '../components/FormikTagsInput';
import { FormikSubmitButton } from '../components/FormikSubmitButton';
import { FormikRadioGroup } from '../components/FormikRadioGroup';
import type { MerchantGatewayConfig } from '../services/gatewayConfigApi';
import { AGGREGATOR_OPTIONS, getAggregatorLogo } from '../constants/icons';
import { useMemo } from 'react';

const validationSchema = yup.object({
  merchantId: yup.string().required('Merchant ID is required'),
  aggregator: yup.string().required('Aggregator is required'),
  checksumKey: yup.string().when('aggregator', (aggregator: any) => {
    return aggregator === 'Billdesk1' || aggregator === 'CashFree'
      ? yup.string().required(`Checksum key is required for ${aggregator}`)
      : yup.string().notRequired();
  }),
  clientId: yup.string().when('aggregator', (aggregator: any) => {
    return aggregator === 'Billdesk2'
      ? yup.string().required('Client ID is required for Billdesk2')
      : yup.string().notRequired();
  }),
  bankId: yup.string().when('aggregator', (aggregator: any) => {
    return aggregator === 'Billdesk1'
      ? yup.string().required('Bank ID is required for Billdesk1')
      : yup.string().notRequired();
  }),
  tags: yup.string().max(255, 'Tags cannot exceed 255 characters').optional(),
});

interface FormValues {
  merchantId: string;
  checksumKey: string;
  aggregator?: MerchantGatewayConfig['aggregator'];
  clientId?: string;
  bankId?: string;
  fundCode?: string;
  tags?: string;
}

export default function MerchantConfigDetail({
  configId,
  onClose,
  amc,
}: {
  amc: any;
  configId?: string;
  onClose?: () => void;
}) {
  const isNew = !configId;
  const { amcId } = useAMC();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { data: merchantConfigs } = useGetMerchantConfigsByAMCQuery(amcId!);
  const [createMerchantConfig] = useCreateMerchantConfigMutation();
  const [updateMerchantConfig] = useUpdateMerchantConfigMutation();

  // Fetch all available aggregators from backend enum
  const { data: allAggregatorsData } = useGetAllAggregatorsQuery();

  // Get all available aggregators for selection
  const aggregatorOptions = useMemo(() => {
    let options =
      allAggregatorsData?.aggregators && allAggregatorsData.aggregators.length > 0
        ? allAggregatorsData.aggregators
        : AGGREGATOR_OPTIONS;

    return options;
  }, [allAggregatorsData]);

  const existingConfig = merchantConfigs?.find((config) => config.id === configId);

  const initialValues: FormValues = {
    merchantId: existingConfig?.merchantId || '',
    checksumKey: existingConfig?.checksumKey || '',
    aggregator: existingConfig?.aggregator,
    clientId: existingConfig?.clientId || '',
    bankId: existingConfig?.bankId || '',
    fundCode: existingConfig?.fundCode || '',
    tags: existingConfig?.tags || '',
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      await createMerchantConfig({
        ...values,
        amc: { id: amcId! },
        aggregator: values.aggregator!,
        clientId: values.clientId || '',
      }).unwrap();
      enqueueSnackbar('Merchant configuration saved successfully', { variant: 'success' });
      navigate(`/amcs/${amcId}/merchant-configs`);
      onClose?.();
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to save merchant configuration', {
        variant: 'error',
      });
      onClose?.();
    }
  };

  const handleUpdateMerchantConfig = async (values: FormValues) => {
    try {
      await updateMerchantConfig({
        ...values,
        id: configId!,
      }).unwrap();
      enqueueSnackbar('Merchant configuration updated successfully', { variant: 'success' });
      navigate(`/amcs/${amcId}/merchant-configs`);
    } catch (error: any) {
      enqueueSnackbar(error.data?.message || 'Failed to update merchant configuration', {
        variant: 'error',
      });
    }
    onClose?.();
  };

  const renderAggregatorLabel = (option: { value: string; label: string }) => {
    const LogoComponent = getAggregatorLogo(option.value);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <img src={LogoComponent} alt={option.label} height={20} />
        <span>{option.label}</span>
      </Box>
    );
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <Box sx={{ maxWidth: 800, mx: 'auto', p: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <FormikRadioGroup
                    name="aggregator"
                    label="Aggregator"
                    options={aggregatorOptions}
                    labelRenderer={renderAggregatorLabel}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField name="merchantId" label="Merchant ID" fullWidth />
                </Grid>

                {(values.aggregator === 'Billdesk1' || values.aggregator === 'CashFree') && (
                  <Grid item xs={12} md={6}>
                    <FormikTextField name="checksumKey" label="Checksum Key" fullWidth />
                  </Grid>
                )}

                {['Billdesk2', 'CamsPay', 'ePay'].includes(values.aggregator || '') && (
                  <Grid item xs={12} md={6}>
                    <FormikTextField name="clientId" label="Client ID" fullWidth />
                  </Grid>
                )}

                {values.aggregator === 'Billdesk1' && (
                  <Grid item xs={12} md={6}>
                    <FormikTextField name="bankId" label="Bank ID" fullWidth />
                  </Grid>
                )}

                {amc?.isRTA && (
                  <Grid item xs={12} md={6}>
                    <FormikTextField name="fundCode" label="Fund Code" fullWidth />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <FormikTagsInput name="tags" label="Tags" />
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  {isNew ? (
                    <FormikSubmitButton>Save Configuration</FormikSubmitButton>
                  ) : (
                    <FormikSubmitButton
                      type="button"
                      onClick={() => handleUpdateMerchantConfig(values)}
                    >
                      Update Configuration
                    </FormikSubmitButton>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
